import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import PasswordInput from './PasswordInput';

interface LoginLocationState {
  resetSuccess?: boolean;
}

interface LoginFormData {
  username: string;
  password: string;
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  color: '#cccccc',
  marginBottom: '8px',
  fontSize: '14px',
  fontWeight: 500,
};

const inputStyle = (invalid: boolean): React.CSSProperties => ({
  width: '100%',
  background: '#2a2a2a',
  border: `1px solid ${invalid ? '#f44336' : '#4a4a4a'}`,
  borderRadius: '4px',
  color: '#ffffff',
  padding: '10px 12px',
  fontSize: '14px',
  outline: 'none',
});

const errorTextStyle: React.CSSProperties = {
  marginTop: '6px',
  color: '#f44336',
  fontSize: '13px',
};

const LoginForm: React.FC = () => {
  const {
    register,
    handleSubmit,
    setFocus,
    formState: { errors },
  } = useForm<LoginFormData>({ mode: 'onTouched' });
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const resetSuccess = (location.state as LoginLocationState | null)?.resetSuccess;

  useEffect(() => {
    setFocus('username');
  }, [setFocus]);

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setError('');

    try {
      await login(data.username.trim(), data.password);
      navigate('/dive-in');
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === 'string'
          ? detail
          : detail?.message || 'Login failed. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="app-layout min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="app-brand" style={{ fontSize: '32px', marginBottom: '16px' }}>
            babylon.markets
          </div>
          <h2
            className="text-3xl font-extrabold"
            style={{ color: '#ffffff', marginBottom: '8px' }}
          >
            Sign in to your account
          </h2>
          <p className="text-sm" style={{ color: '#888888' }}>
            New to babylon.markets?{' '}
            <Link to="/register" className="font-medium" style={{ color: '#2962ff' }}>
              Create an account
            </Link>
          </p>
        </div>

        <div className="app-card">
          {resetSuccess && (
            <div
              role="status"
              aria-live="polite"
              style={{
                background: 'rgba(34, 197, 94, 0.08)',
                border: '1px solid rgba(34, 197, 94, 0.4)',
                color: '#22c55e',
                padding: '10px 12px',
                borderRadius: '4px',
                fontSize: '13px',
                textAlign: 'center',
                marginBottom: '16px',
              }}
            >
              Password updated. Sign in with your new password.
            </div>
          )}
          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className="space-y-4">
              <div>
                <label htmlFor="username" style={labelStyle}>
                  Email or username
                </label>
                <input
                  id="username"
                  type="text"
                  autoComplete="username"
                  placeholder="you@example.com"
                  aria-invalid={errors.username ? true : undefined}
                  aria-describedby={errors.username ? 'login-username-error' : undefined}
                  style={inputStyle(!!errors.username)}
                  {...register('username', { required: 'Email or username is required' })}
                />
                {errors.username && (
                  <p id="login-username-error" style={errorTextStyle}>
                    {errors.username.message}
                  </p>
                )}
              </div>

              <div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                  }}
                >
                  <label htmlFor="password" style={labelStyle}>
                    Password
                  </label>
                  <Link
                    to="/forgot-password"
                    style={{ color: '#2962ff', fontSize: '12px' }}
                  >
                    Forgot password?
                  </Link>
                </div>
                <PasswordInput
                  id="password"
                  autoComplete="current-password"
                  placeholder="Your password"
                  invalid={!!errors.password}
                  aria-describedby={errors.password ? 'login-password-error' : undefined}
                  {...register('password', { required: 'Password is required' })}
                />
                {errors.password && (
                  <p id="login-password-error" style={errorTextStyle}>
                    {errors.password.message}
                  </p>
                )}
              </div>
            </div>

            {error && (
              <div
                role="alert"
                aria-live="polite"
                style={{
                  background: 'rgba(244, 67, 54, 0.08)',
                  border: '1px solid rgba(244, 67, 54, 0.4)',
                  color: '#f88',
                  padding: '10px 12px',
                  borderRadius: '4px',
                  fontSize: '13px',
                  textAlign: 'center',
                }}
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary"
              style={{
                width: '100%',
                opacity: isLoading ? 0.5 : 1,
                cursor: isLoading ? 'not-allowed' : 'pointer',
              }}
            >
              {isLoading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
